import React, { Suspense } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/History'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InternalCreateCustomerInput } from '@core/api/generated'
import Dropdown from '@core/components/dropdown/dropdown'
import { TextField, TextFieldProps } from '@core/components/textfield/textfield'
import { addSnack } from '@core/snack/snack-state'

import { FormValues } from '../main-types'
import { useCreateCustomer, useFeatureFlagDefaults } from './admin-api'
import { FeatureFlagHistoryModal } from './admin-feature-flag-defaults'
import { AdminSettings } from './admin-settings-state'
import { FeatureFlagsSection } from './feature-flags-section'
import {
  defaultFeatureValues,
  handleFeatureFlagsChange,
  useFeatureFlagsProps,
} from './use-feature-flags-props'

export const categoryOptions = [
  { value: 'regular', name: 'Regular' },
  { value: 'demo', name: 'Demo' },
  { value: 'internal', name: 'Internal' },
]

type Values = InternalCreateCustomerInput & FormValues

const AdminAddCustomerContent = ({ adminSettings }: { adminSettings: AdminSettings }) => {
  const { t } = useTranslation('legacy', { keyPrefix: 'admin.add_customer' })
  const { t: tCommon } = useTranslation('common', { keyPrefix: 'admin.add_customer' })
  const { handleClose } = adminSettings
  const { data: flagDefaults } = useFeatureFlagDefaults()
  const [historyOpen, setHistoryOpen] = React.useState(false)

  const createCustomerMutation = useCreateCustomer()

  const form = useForm<Values>({
    defaultValues: {
      name: '',
      identifier: '',
      category: 'regular',
      hasOptionRationale: flagDefaults?.features?.has_option_rationale ?? true,
      features: (flagDefaults?.features as FormValues['features']) ?? defaultFeatureValues,
    },
  })
  const { register, formState, handleSubmit, control, watch, setValue } = form
  const categoryField = useController({ name: 'category', control })

  const features = watch('features')
  const hasOptionRationale = watch('hasOptionRationale')
  const values: FormValues = { features, hasOptionRationale }

  const getTextFieldProps = (field: 'name' | 'identifier'): TextFieldProps => {
    const error = formState.errors[field]
    let message = error?.message

    if (error?.type === 'maxLength') {
      message =
        field === 'identifier' ? t('max_length', { length: 6 }) : t('max_length', { length: 128 })
    } else if (error?.type === 'minLength') {
      message =
        field === 'identifier' ? t('min_length', { length: 2 }) : t('min_length', { length: 3 })
    }

    return {
      error: Boolean(error),
      helperText: message,
    }
  }

  const handleFeatureChange = React.useCallback(
    (field: string, value: boolean | string | undefined) => {
      const updated = handleFeatureFlagsChange(values, field, value)
      setValue('features', updated.features, { shouldDirty: true })
      setValue('hasOptionRationale', updated.hasOptionRationale, { shouldDirty: true })
    },
    [values, setValue],
  )

  const { getCheckboxProps, getFilenameConventionProps } = useFeatureFlagsProps({
    values,
    onChange: handleFeatureChange,
  })

  const onSubmit = async (formValues: Values) => {
    const result = await createCustomerMutation.mutateAsync({
      name: formValues.name,
      identifier: formValues.identifier,
      category: formValues.category,
      features: formValues.features,
      hasOptionRationale: formValues.hasOptionRationale,
    })

    if (result.error?.handled) {
      const field = result.error.field as 'name' | 'identifier'
      form.setError(field, { message: result.error.message })
      return
    }

    if (result.error) {
      addSnack({
        message: t('error_creating_customer'),
        severity: 'error',
      })
      return
    }

    addSnack({ message: t('customer_added'), severity: 'success' })

    handleClose()
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <TextField
          autoFocus
          required
          {...register('name', {
            maxLength: 128,
            minLength: 3,
            required: t('required'),
          })}
          {...getTextFieldProps('name')}
          label={t('name')}
          aria-label={tCommon('aria_customer_name')}
          fullWidth
        />
        <Box mt={2}>
          <TextField
            required
            {...register('identifier', {
              maxLength: 6,
              minLength: 2,
              required: t('required'),
            })}
            {...getTextFieldProps('identifier')}
            label={t('identifier')}
            aria-label={tCommon('aria_customer_identifier')}
            fullWidth
          />
        </Box>
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t('category')}
          </Typography>
          <Dropdown
            value={categoryOptions.find((o) => o.value === categoryField.field.value) ?? null}
            onChange={(option) => categoryField.field.onChange(option.value)}
            options={categoryOptions}
          />
        </Box>

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 3,
            px: 3,
            '&:before': { display: 'none' },
            bgcolor: 'transparent',
            border: 1,
            borderRadius: 1,
            borderColor: 'divider',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 0,
              minHeight: 'auto',
              '& .MuiAccordionSummary-content': { my: 1.5, flexDirection: 'column' },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2">{t('advanced_settings')}</Typography>
              <Tooltip title={tCommon('tooltip_view_history')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setHistoryOpen(true)
                  }}
                  sx={{ p: 0.5 }}
                  aria-label={tCommon('aria_view_history')}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {flagDefaults?.createdBy && flagDefaults?.createdAt && (
              <Typography variant="caption" color="text.secondary">
                {t('last_updated_by', {
                  firstName: flagDefaults.createdBy.firstName,
                  lastName: flagDefaults.createdBy.lastName,
                  date: new Date(flagDefaults.createdAt).toLocaleString(),
                })}
              </Typography>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <FeatureFlagsSection
              getCheckboxProps={getCheckboxProps}
              getFilenameConventionProps={getFilenameConventionProps}
            />
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={formState.isSubmitting}>
          {t('cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          disabled={formState.isSubmitting || !formState.isDirty}
          loading={formState.isSubmitting}
          type="submit"
        >
          {t('add_customer')}
        </LoadingButton>
      </DialogActions>

      <React.Suspense fallback={null}>
        <FeatureFlagHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      </React.Suspense>
    </Box>
  )
}

export const AdminAddCustomer = ({ adminSettings }: { adminSettings: AdminSettings }) => (
  <Suspense fallback={<CircularProgress size={24} />}>
    <AdminAddCustomerContent adminSettings={adminSettings} />
  </Suspense>
)
